import { useNavigate } from 'react-router-dom'
import {
  UserPlus,
  ArrowLeftRight,
  UserMinus,
  UserX,
  UserCheck,
  LogOut,
  XCircle,
  CheckCircle,
  AlertTriangle,
  PlayCircle,
  Repeat,
  Clock,
  X,
  Mail,
  Landmark,
  BadgeCheck,
  TrendingDown,
} from 'lucide-react'
import { NotificationType } from '@fp3/shared'
import type { NotificationResponseDto } from '@fp3/shared'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipPortal } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils/cn'
import { formatRelativeTime } from '@/lib/utils/formatters'
import { useMarkAsRead } from '../hooks/useMarkAsRead'
import { useMarkAsUnread } from '../hooks/useMarkAsUnread'
import { useDeleteNotification } from '../hooks/useDeleteNotification'

const iconMap: Record<string, React.ElementType> = {
  [NotificationType.GROUP_INVITATION]: UserPlus,
  [NotificationType.SETTLEMENT_REQUEST]: ArrowLeftRight,
  [NotificationType.SETTLEMENT_CONFIRMED]: CheckCircle,
  [NotificationType.SETTLEMENT_DECLINED]: XCircle,
  [NotificationType.SETTLEMENT_ROUND_STARTED]: PlayCircle,
  [NotificationType.EXPENSE_FLAGGED]: AlertTriangle,
  [NotificationType.MEMBER_JOINED]: UserCheck,
  [NotificationType.MEMBER_LEFT]: LogOut,
  [NotificationType.MEMBER_REMOVED]: UserMinus,
  [NotificationType.INVITATION_DECLINED]: UserX,
  [NotificationType.RECURRENT_EXPENSE_CREATED]: Repeat,
  [NotificationType.RECURRENT_EXPENSE_CREATED_NO_AMOUNT]: AlertTriangle,
  [NotificationType.RECURRENT_EXPENSE_UPCOMING]: Clock,
  [NotificationType.RECURRENT_INCOME_CREATED]: Repeat,
  [NotificationType.RECURRENT_INCOME_CREATED_NO_AMOUNT]: AlertTriangle,
  [NotificationType.RECURRENT_INCOME_UPCOMING]: Clock,
  [NotificationType.DEPOSIT_INTEREST_CREATED]: Landmark,
  [NotificationType.DEPOSIT_MATURED]: BadgeCheck,
  [NotificationType.PORTFOLIO_DRAWDOWN]: TrendingDown,
  [NotificationType.QUICK_EXPENSE_NO_DESCRIPTION]: AlertTriangle,
}

interface NotificationCardProps {
  notification: NotificationResponseDto
  index: number
}

export const NotificationCard = ({ notification, index }: NotificationCardProps) => {
  const navigate = useNavigate()
  const markAsRead = useMarkAsRead()
  const markAsUnread = useMarkAsUnread()
  const deleteNotification = useDeleteNotification()

  const Icon = iconMap[notification.type] ?? UserPlus

  // notifications created without an amount need the user's attention to set one
  const needsAttention =
    notification.type === NotificationType.RECURRENT_INCOME_CREATED_NO_AMOUNT ||
    notification.type === NotificationType.RECURRENT_EXPENSE_CREATED_NO_AMOUNT

  const handleClick = () => {
    if (!notification.read) {
      markAsRead.mutate(notification.id)
    }
    // validate that the link is a safe relative path
    if (
      notification.link &&
      notification.link.startsWith('/') &&
      !notification.link.includes('://')
    ) {
      navigate(notification.link)
    }
  }

  const handleMarkUnread = (e: React.MouseEvent) => {
    e.stopPropagation()
    markAsUnread.mutate(notification.id)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    deleteNotification.mutate(notification.id)
  }

  return (
    <Card
      className={cn(
        'p-4 cursor-pointer transition-colors hover:bg-muted/50',
        !notification.read &&
          (needsAttention
            ? 'border-l-4 border-l-destructive bg-destructive/5'
            : 'border-l-4 border-l-primary bg-primary/5')
      )}
      onClick={handleClick}
      data-testid={`notifications-card-${index}`}
    >
      {!notification.read && (
        <span className="sr-only" data-testid={`notifications-card-unread-${index}`} />
      )}
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p
              className={`text-sm ${!notification.read ? 'font-semibold' : 'font-medium'}`}
              data-testid={`notifications-card-title-${index}`}
            >
              {notification.title}
            </p>
            <span className="text-xs text-muted-foreground shrink-0">
              {formatRelativeTime(notification.createdAt)}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {notification.read && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={handleMarkUnread}
                  data-testid={`notifications-card-mark-unread-${index}`}
                >
                  <Mail className="h-4 w-4" />
                  <span className="sr-only">Mark as unread</span>
                </Button>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent>Mark as unread</TooltipContent>
              </TooltipPortal>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={handleDelete}
                data-testid={`notifications-card-delete-${index}`}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Delete</span>
              </Button>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent>Delete</TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </div>
      </div>
    </Card>
  )
}
